/**
 * `useIsAdmin` hook (round-22 Phase D, `public-website` spec).
 *
 * Returns `true` exactly when the current Cognito ID token
 * carries `"admin"` in the `cognito:groups` claim — the group
 * membership added by the `admin` Cognito group on
 * `dvgupv2-public-pool-{region}-{stage}` (round-22 Phase A.1).
 *
 * Admin group membership is granted OUT-OF-BAND by the operator
 * via the `grant-admin.sh` wrapper (round-22 Phase E). The SPA
 * never presents an "enable admin" affordance to end users — see
 * `.kiro/steering/no-vestigial-artifacts.md` for the deletion
 * of the previous client-toggleable admin flag. Discoverability
 * is fine (an admin can see the admin surface exists); the
 * capability is gated at the Cognito authorizer on the admin
 * API gateway, which independently enforces the group check per
 * `services/src/api-layer/managers/require-admin-group.ts`.
 *
 * The hook is pure over the auth slice's `idToken` field — a
 * change in the token (sign-in, sign-out, group-membership
 * update on next sign-in) re-renders every consumer. Zero I/O.
 */

import { useAppSelector } from '../../store/hooks';
import { decodeJwt } from './jwt-decoder';

/**
 * True iff the current session's ID token has `admin` in
 * `cognito:groups`. False for unauthenticated visitors and
 * authenticated non-admins alike.
 */
export function useIsAdmin(): boolean {
  const idToken = useAppSelector((state) => state.auth.idToken);
  if (idToken === undefined || idToken.length === 0) return false;
  const payload = decodeJwt(idToken);
  if (payload === null) return false;
  const groups = payload['cognito:groups'];
  if (!Array.isArray(groups)) return false;
  return groups.includes('admin');
}
